import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  Wallet, 
  CreditCard, 
  Building2, 
  ArrowUpRight, 
  ArrowDownLeft,
  Clock,
  CheckCircle,
  XCircle,
  Copy,
  Loader2,
  RefreshCw,
  History,
  AlertCircle,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WalletPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language, tx } = useAdminTranslation();
  const { toast } = useToast();
  
  const [wallet, setWallet] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRechargeDialog, setShowRechargeDialog] = useState(false);
  const [rechargeMethod, setRechargeMethod] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [transferReference, setTransferReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(null);
  const [showMissingDataDialog, setShowMissingDataDialog] = useState(false);
  const [missingDataMessage, setMissingDataMessage] = useState('');
  const [userProfile, setUserProfile] = useState(null);

  // Traducciones multi-idioma
  const t = {
    es: {
      myWallet: 'Mi Monedero',
      manageBalance: 'Gestiona tu saldo y recargas',
      availableBalance: 'Saldo disponible',
      topUp: 'Recargar',
      transactions: 'Movimientos',
      myTopUps: 'Mis Recargas',
      transactionHistory: 'Historial de Movimientos',
      noTransactions: 'No hay movimientos aún',
      myTopUpRequests: 'Mis Solicitudes de Recarga',
      noTopUps: 'No hay recargas aún',
      topUpWallet: 'Recargar Monedero',
      selectMethodAmount: 'Selecciona el método de pago y el importe a recargar',
      paymentMethod: 'Método de pago',
      selectMethod: 'Selecciona un método',
      bankTransfer: 'Transferencia Bancaria',
      destinationBank: 'Banco destino',
      selectBank: 'Selecciona banco',
      transferDetails: 'Datos para la transferencia:',
      emailNote: '📧 Al crear la solicitud, te enviaremos estos datos por email.',
      amount: 'Importe (€)',
      baseAmount: 'Importe base',
      iva: 'IVA (21%)',
      totalToPay: 'Total a pagar',
      reference: 'Referencia/Concepto (opcional)',
      yourReference: 'Tu referencia para identificar el pago',
      cancel: 'Cancelar',
      requestTopUp: 'Solicitar Recarga',
      processing: 'Procesando...',
      requestCreated: 'Solicitud Creada',
      makeTransfer: 'Realiza la transferencia a la siguiente cuenta bancaria:',
      amountToTransfer: 'Importe a transferir:',
      taxNotice: 'Cuando recargue el saldo se generará una factura. Debido a los impuestos y/o comisiones para el método de pago utilizado, el importe total a pagar en la pasarela de pago será mayor que la cantidad indicada aquí.',
      pending: 'Pendiente',
      approved: 'Aprobada',
      rejected: 'Rechazada',
    },
    en: {
      myWallet: 'My Wallet',
      manageBalance: 'Manage your balance and top-ups',
      availableBalance: 'Available balance',
      topUp: 'Top Up',
      transactions: 'Transactions',
      myTopUps: 'My Top-Ups',
      transactionHistory: 'Transaction History',
      noTransactions: 'No transactions yet',
      myTopUpRequests: 'My Top-Up Requests',
      noTopUps: 'No top-ups yet',
      topUpWallet: 'Top Up Wallet',
      selectMethodAmount: 'Select payment method and amount to top up',
      paymentMethod: 'Payment method',
      selectMethod: 'Select a method',
      bankTransfer: 'Bank Transfer',
      destinationBank: 'Destination bank',
      selectBank: 'Select bank',
      transferDetails: 'Bank transfer details:',
      emailNote: '📧 When you create the request, we will send these details by email.',
      amount: 'Amount (€)',
      baseAmount: 'Base amount',
      iva: 'VAT (21%)',
      totalToPay: 'Total to pay',
      reference: 'Reference (optional)',
      yourReference: 'Your reference',
      cancel: 'Cancel',
      requestTopUp: 'Request Top-Up',
      processing: 'Processing...',
      requestCreated: 'Request Created',
      makeTransfer: 'Make the transfer to the following bank account:',
      amountToTransfer: 'Amount to transfer:',
      taxNotice: 'When you top up your balance, an invoice will be generated. Due to taxes and/or fees for the payment method used, the total amount to pay will be higher than the amount indicated here.',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    de: {
      myWallet: 'Meine Brieftasche',
      manageBalance: 'Verwalten Sie Ihr Guthaben und Aufladungen',
      availableBalance: 'Verfügbares Guthaben',
      topUp: 'Aufladen',
      transactions: 'Transaktionen',
      myTopUps: 'Meine Aufladungen',
      transactionHistory: 'Transaktionsverlauf',
      noTransactions: 'Noch keine Transaktionen',
      myTopUpRequests: 'Meine Aufladeanfragen',
      noTopUps: 'Noch keine Aufladungen',
      topUpWallet: 'Brieftasche aufladen',
      selectMethodAmount: 'Wählen Sie Zahlungsmethode und Betrag',
      paymentMethod: 'Zahlungsmethode',
      selectMethod: 'Methode auswählen',
      bankTransfer: 'Banküberweisung',
      destinationBank: 'Zielbank',
      selectBank: 'Bank auswählen',
      transferDetails: 'Überweisungsdaten:',
      emailNote: '📧 Bei Erstellung der Anfrage senden wir Ihnen diese Daten per E-Mail.',
      amount: 'Betrag (€)',
      baseAmount: 'Basisbetrag',
      iva: 'MwSt (21%)',
      totalToPay: 'Zu zahlen',
      reference: 'Referenz (optional)',
      yourReference: 'Ihre Referenz',
      cancel: 'Abbrechen',
      requestTopUp: 'Aufladung anfordern',
      processing: 'Verarbeitung...',
      requestCreated: 'Anfrage erstellt',
      makeTransfer: 'Überweisen Sie auf folgendes Bankkonto:',
      amountToTransfer: 'Zu überweisender Betrag:',
      taxNotice: 'Bei Aufladung wird eine Rechnung erstellt. Aufgrund von Steuern und/oder Gebühren wird der Gesamtbetrag höher sein als der hier angegebene Betrag.',
      pending: 'Ausstehend',
      approved: 'Genehmigt',
      rejected: 'Abgelehnt',
    },
    fr: {
      myWallet: 'Mon Portefeuille',
      manageBalance: 'Gérez votre solde et vos recharges',
      availableBalance: 'Solde disponible',
      topUp: 'Recharger',
      transactions: 'Transactions',
      myTopUps: 'Mes Recharges',
      transactionHistory: 'Historique des transactions',
      noTransactions: 'Aucune transaction',
      myTopUpRequests: 'Mes demandes de recharge',
      noTopUps: 'Aucune recharge',
      topUpWallet: 'Recharger le portefeuille',
      selectMethodAmount: 'Sélectionnez le mode de paiement et le montant',
      paymentMethod: 'Mode de paiement',
      selectMethod: 'Sélectionner une méthode',
      bankTransfer: 'Virement bancaire',
      destinationBank: 'Banque de destination',
      selectBank: 'Sélectionner la banque',
      transferDetails: 'Coordonnées bancaires:',
      emailNote: '📧 Lors de la création de la demande, nous vous enverrons ces détails par email.',
      amount: 'Montant (€)',
      baseAmount: 'Montant de base',
      iva: 'TVA (21%)',
      totalToPay: 'Total à payer',
      reference: 'Référence (optionnel)',
      yourReference: 'Votre référence',
      cancel: 'Annuler',
      requestTopUp: 'Demander une recharge',
      processing: 'Traitement...',
      requestCreated: 'Demande créée',
      makeTransfer: 'Effectuez le virement sur le compte bancaire suivant:',
      amountToTransfer: 'Montant à transférer:',
      taxNotice: 'Lors de la recharge, une facture sera générée. En raison des taxes et/ou frais, le montant total à payer sera supérieur au montant indiqué ici.',
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Rejetée',
    },
    it: {
      myWallet: 'Il Mio Portafoglio',
      manageBalance: 'Gestisci il tuo saldo e le ricariche',
      availableBalance: 'Saldo disponibile',
      topUp: 'Ricarica',
      transactions: 'Transazioni',
      myTopUps: 'Le Mie Ricariche',
      transactionHistory: 'Cronologia transazioni',
      noTransactions: 'Nessuna transazione',
      myTopUpRequests: 'Le mie richieste di ricarica',
      noTopUps: 'Nessuna ricarica',
      topUpWallet: 'Ricarica portafoglio',
      selectMethodAmount: 'Seleziona metodo di pagamento e importo',
      paymentMethod: 'Metodo di pagamento',
      selectMethod: 'Seleziona un metodo',
      bankTransfer: 'Bonifico bancario',
      destinationBank: 'Banca di destinazione',
      selectBank: 'Seleziona banca',
      transferDetails: 'Dati per il bonifico:',
      emailNote: '📧 Creando la richiesta, ti invieremo questi dati via email.',
      amount: 'Importo (€)',
      baseAmount: 'Importo base',
      iva: 'IVA (21%)',
      totalToPay: 'Totale da pagare',
      reference: 'Riferimento (opzionale)',
      yourReference: 'Il tuo riferimento',
      cancel: 'Annulla',
      requestTopUp: 'Richiedi ricarica',
      processing: 'Elaborazione...',
      requestCreated: 'Richiesta creata',
      makeTransfer: 'Effettua il bonifico sul seguente conto:',
      amountToTransfer: 'Importo da trasferire:',
      taxNotice: 'Quando ricarichi, verrà generata una fattura. A causa di tasse e/o commissioni, l\'importo totale sarà superiore a quello indicato qui.',
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
    },
    pt: {
      myWallet: 'Minha Carteira',
      manageBalance: 'Gerencie seu saldo e recargas',
      availableBalance: 'Saldo disponível',
      topUp: 'Recarregar',
      transactions: 'Transações',
      myTopUps: 'Minhas Recargas',
      transactionHistory: 'Histórico de transações',
      noTransactions: 'Nenhuma transação ainda',
      myTopUpRequests: 'Minhas solicitações de recarga',
      noTopUps: 'Nenhuma recarga ainda',
      topUpWallet: 'Recarregar Carteira',
      selectMethodAmount: 'Selecione o método de pagamento e o valor',
      paymentMethod: 'Método de pagamento',
      selectMethod: 'Selecione um método',
      bankTransfer: 'Transferência Bancária',
      destinationBank: 'Banco de destino',
      selectBank: 'Selecione o banco',
      transferDetails: 'Dados para transferência:',
      emailNote: '📧 Ao criar a solicitação, enviaremos estes dados por email.',
      amount: 'Valor (€)',
      baseAmount: 'Valor base',
      iva: 'IVA (21%)',
      totalToPay: 'Total a pagar',
      reference: 'Referência (opcional)',
      yourReference: 'Sua referência',
      cancel: 'Cancelar',
      requestTopUp: 'Solicitar Recarga',
      processing: 'Processando...',
      requestCreated: 'Solicitação Criada',
      makeTransfer: 'Faça a transferência para a seguinte conta:',
      amountToTransfer: 'Valor a transferir:',
      taxNotice: 'Ao recarregar o saldo, uma fatura será gerada. Devido a impostos e/ou taxas, o valor total a pagar será maior que o valor indicado aqui.',
      pending: 'Pendente',
      approved: 'Aprovada',
      rejected: 'Rejeitada',
    },
  };
  const texts = t[language] || t.es;

  // Calcular si el usuario es de España para aplicar IVA
  const isSpanishUser = userProfile?.country === 'España' || userProfile?.country === 'Spain' || 
                        userProfile?.company_country === 'España' || userProfile?.company_country === 'Spain';
  const IVA_RATE = 0.21;
  
  // Calcular importes con IVA
  const baseAmount = parseFloat(rechargeAmount) || 0;
  const ivaAmount = isSpanishUser ? baseAmount * IVA_RATE : 0;
  const totalAmount = baseAmount + ivaAmount;

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  }, [token]);

  const fetchWallet = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWallet(data);
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  }, [token]);

  const fetchBankAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/wallet/bank-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBankAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  }, [token]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/wallet/transactions?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  }, [token]);

  const fetchRecharges = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/wallet/recharges?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecharges(data.recharges || []);
      }
    } catch (error) {
      console.error('Error fetching recharges:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchWallet(),
        fetchBankAccounts(),
        fetchTransactions(),
        fetchRecharges(),
        fetchUserProfile()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchWallet, fetchBankAccounts, fetchTransactions, fetchRecharges, fetchUserProfile]);

  // Si esta página se ha cargado dentro del popup de PayPal (return_url), avisar al opener
  // y cerrar el popup AL INSTANTE para que el usuario no espere.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paypal = params.get('paypal');
    if (paypal === 'success' || paypal === 'cancel') {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: 'paypal_return', result: paypal }, '*');
        }
      } catch (_) { /* noop */ }
      setTimeout(() => {
        try { window.close(); } catch (_) { /* noop */ }
      }, 100);
    }
  }, []);

  // PayPal flow: open popup and poll for capture
  const handlePaypalRecharge = async () => {
    const amountNum = parseFloat(rechargeAmount);
    if (!amountNum || amountNum < 10) {
      toast({
        title: 'Error',
        description: tx('Importe mínimo 10€ para pagar con PayPal', 'Minimum amount is €10 for PayPal'),
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const createRes = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount: amountNum, purpose: 'wallet_recharge' }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo crear la orden PayPal');
      }
      const { order_id, approve_url } = await createRes.json();

      // Open PayPal in a centered popup
      const w = 520, h = 700;
      const left = (window.screen.width - w) / 2;
      const top = (window.screen.height - h) / 2;
      const popup = window.open(
        approve_url,
        'paypal_popup',
        `width=${w},height=${h},left=${left},top=${top}`
      );
      if (!popup) {
        toast({
          title: 'Error',
          description: tx('Desactiva el bloqueador de pop-ups y vuelve a intentarlo', 'Please disable the pop-up blocker and try again'),
          variant: 'destructive',
        });
        setSubmitting(false);
        return;
      }

      // Doble vía de detección (la primera que dispare gana):
      //  A) postMessage del popup cuando carga return_url (más rápido, instantáneo)
      //  B) polling de URL como fallback (si postMessage falla por algún motivo)
      let captured = false;
      const finishCapture = async (success) => {
        if (captured) return;
        captured = true;
        clearInterval(interval);
        window.removeEventListener('message', onMessage);
        try { popup.close(); } catch (_) {}
        if (!success) {
          toast({
            title: tx('Pago cancelado', 'Payment cancelled'),
            description: tx('Has cancelado el pago en PayPal.', 'You cancelled the PayPal payment.'),
            variant: 'destructive',
          });
          setSubmitting(false);
          return;
        }
        try {
          const capRes = await fetch(`${API_URL}/api/paypal/capture/${order_id}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const capData = await capRes.json();
          if (capRes.ok && capData.status === 'COMPLETED') {
            toast({
              title: tx('¡Recarga completada!', 'Top-up complete!'),
              description: tx(
                `Se han abonado ${amountNum.toFixed(2)}€ a tu monedero.`,
                `€${amountNum.toFixed(2)} credited to your wallet.`
              ),
            });
            setShowRechargeDialog(false);
            setRechargeAmount('');
            setRechargeMethod('');
            await Promise.all([fetchRecharges(), fetchWallet(), fetchTransactions()]);
          } else {
            toast({
              title: tx('Pago no completado', 'Payment not completed'),
              description: capData.detail || tx('El pago PayPal se canceló o no se confirmó.', 'PayPal payment was cancelled or not confirmed.'),
              variant: 'destructive',
            });
          }
        } catch (e) {
          toast({ title: 'Error', description: e.message || 'Error capturando pago PayPal', variant: 'destructive' });
        } finally {
          setSubmitting(false);
        }
      };

      const onMessage = (ev) => {
        const data = ev?.data;
        if (data && data.type === 'paypal_return') {
          finishCapture(data.result === 'success');
        }
      };
      window.addEventListener('message', onMessage);

      const interval = setInterval(() => {
        let returnedToOurDomain = null;
        try {
          const href = popup.location.href;
          if (href && href.indexOf('paypal=success') !== -1) returnedToOurDomain = true;
          else if (href && href.indexOf('paypal=cancel') !== -1) returnedToOurDomain = false;
        } catch (_) { /* cross-origin while in paypal.com */ }
        if (returnedToOurDomain !== null) {
          finishCapture(returnedToOurDomain);
        } else if (popup.closed) {
          // Popup cerrado por el usuario sin pasar por return_url → cancelado
          finishCapture(false);
        }
      }, 400);

      // Timeout de seguridad (4 min) por si nada dispara
      setTimeout(() => {
        if (!captured) {
          captured = true;
          clearInterval(interval);
          window.removeEventListener('message', onMessage);
          setSubmitting(false);
          toast({
            title: tx('Tiempo agotado', 'Timeout'),
            description: tx('No se detectó la respuesta de PayPal.', 'PayPal response not detected.'),
            variant: 'destructive',
          });
        }
      }, 240000);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setSubmitting(false);
    }
  };

  const handleRecharge = async () => {
    if (!rechargeAmount || parseFloat(rechargeAmount) <= 0) {
      toast({
        title: 'Error',
        description: tx('Por favor, introduce un importe válido', 'Please enter a valid amount'),
        variant: 'destructive'
      });
      return;
    }

    if (rechargeMethod === 'bank_transfer' && !selectedBank) {
      toast({
        title: 'Error',
        description: tx('Por favor, selecciona un banco', 'Please select a bank'),
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wallet/recharge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseFloat(rechargeAmount),
          method: rechargeMethod,
          bank_selected: selectedBank,
          transfer_reference: transferReference,
          iva_amount: ivaAmount,
          total_amount: totalAmount,
          is_spanish_user: isSpanishUser
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: '¡Solicitud creada!',
          description: data.message
        });
        setShowRechargeDialog(false);
        
        if (rechargeMethod === 'bank_transfer') {
          setShowBankDetails(data.bank_info);
        }
        
        // Refresh data
        await Promise.all([fetchRecharges(), fetchWallet()]);
        
        // Reset form
        setRechargeAmount('');
        setSelectedBank('');
        setTransferReference('');
        setRechargeMethod('');
      } else {
        // Error handling - body may already be consumed by interceptors
        // Try multiple approaches to get error message
        let errorDetail = '';
        
        // First, check if this is a 400 error which is typically billing related
        if (response.status === 400) {
          // For 400 errors during recharge, assume it's a billing data issue
          // Make a separate call to check profile completeness
          try {
            const profileRes = await fetch(`${API_URL}/api/profile`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (profileRes.ok) {
              const profile = await profileRes.json();
              const missingFields = [];
              
              if (!profile.full_name && !profile.company_name) missingFields.push('Nombre');
              if (!profile.country && !profile.company_country) missingFields.push('País');
              if (!profile.postal_code && !profile.company_postal_code) missingFields.push('Código Postal');
              if (!profile.address && !profile.street && !profile.company_street && !profile.fiscal_address) missingFields.push('Dirección');
              
              if (missingFields.length > 0) {
                errorDetail = `Debes completar los siguientes campos en 'Mi Cuenta': ${missingFields.join(', ')}`;
              }
            }
          } catch (e) {
            // Fallback to generic billing error message
          }
          
          if (!errorDetail) {
            errorDetail = "Debes completar tus datos de facturación en 'Mi Cuenta' antes de recargar.";
          }
        } else {
          errorDetail = 'Error al crear la solicitud';
        }
        
        // Check if it's a billing data error
        const isBillingError = response.status === 400 && errorDetail.toLowerCase().includes('mi cuenta');
        
        if (isBillingError) {
          setShowRechargeDialog(false);
          setMissingDataMessage(errorDetail);
          setTimeout(() => {
            setShowMissingDataDialog(true);
          }, 100);
        } else {
          toast({
            title: 'Error',
            description: errorDetail,
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error de conexión',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copiado',
      description: 'Texto copiado al portapapeles'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Aprobada</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Rechazada</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Pendiente</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'recharge':
        return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
      case 'purchase':
        return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      case 'refund':
        return <ArrowDownLeft className="w-4 h-4 text-blue-600" />;
      default:
        return <RefreshCw className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6 text-primary" />
            {texts.myWallet}
          </h1>
          <p className="text-muted-foreground">
            {texts.manageBalance}
          </p>
        </div>

        {/* Balance Card */}
        <Card className="mb-8 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {texts.availableBalance}
                </p>
                <p className="text-4xl font-bold text-primary">
                  {wallet?.balance?.toFixed(2) || '0.00'} €
                </p>
                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                  <span>Total recargado: {wallet?.total_recharged?.toFixed(2) || '0.00'} €</span>
                  <span>Total gastado: {wallet?.total_spent?.toFixed(2) || '0.00'} €</span>
                </div>
              </div>
              <Button size="lg" onClick={() => setShowRechargeDialog(true)}>
                <CreditCard className="w-4 h-4 mr-2" />
                {texts.topUp}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="transactions" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="transactions">
              <History className="w-4 h-4 mr-2" />
              {texts.transactions}
            </TabsTrigger>
            <TabsTrigger value="recharges">
              <CreditCard className="w-4 h-4 mr-2" />
              {texts.myTopUps}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>{texts.transactionHistory}</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {texts.noTransactions}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getTransactionIcon(tx.type)}
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.created_at).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)} €
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Saldo: {tx.balance_after.toFixed(2)} €
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recharges">
            <Card>
              <CardHeader>
                <CardTitle>{texts.myTopUpRequests}</CardTitle>
              </CardHeader>
              <CardContent>
                {recharges.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {texts.noTopUps}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {recharges.map((r) => (
                      <div key={r.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {r.method === 'bank_transfer' ? (
                              <Building2 className="w-4 h-4 text-blue-600" />
                            ) : (
                              <CreditCard className="w-4 h-4 text-blue-600" />
                            )}
                            <span className="font-medium">
                              {r.method === 'bank_transfer' ? `${texts.bankTransfer} - ${r.bank_selected}` : 'PayPal'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {new Date(r.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : 'en-US', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {r.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">Motivo: {r.rejection_reason}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{r.amount.toFixed(2)} €</p>
                          {getStatusBadge(r.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Recharge Dialog */}
        <Dialog open={showRechargeDialog} onOpenChange={setShowRechargeDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{texts.topUpWallet}</DialogTitle>
              <DialogDescription>
                {texts.selectMethodAmount}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{texts.paymentMethod}</Label>
                <Select value={rechargeMethod} onValueChange={setRechargeMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder={texts.selectMethod} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {texts.bankTransfer}
                      </div>
                    </SelectItem>
                    <SelectItem value="paypal">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        PayPal / {tx('Tarjeta', 'Card')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {rechargeMethod === 'bank_transfer' && (
                <div className="space-y-2">
                  <Label>{texts.destinationBank}</Label>
                  <Select value={selectedBank} onValueChange={setSelectedBank}>
                    <SelectTrigger>
                      <SelectValue placeholder={texts.selectBank} />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((bank) => (
                        <SelectItem key={bank.bank} value={bank.bank}>
                          {bank.bank} - {bank.holder}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Show bank details when bank is selected */}
              {rechargeMethod === 'bank_transfer' && selectedBank && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <p className="font-medium text-blue-800 text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {texts.transferDetails}
                  </p>
                  {bankAccounts.filter(b => b.bank === selectedBank).map((bank) => (
                    <div key={bank.bank} className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Banco:</span>
                        <span className="font-medium">{bank.bank}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Titular:</span>
                        <span className="font-medium">{bank.holder}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">IBAN:</span>
                        <div className="flex items-center gap-1">
                          <span className="font-mono font-medium text-xs">{bank.iban}</span>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(bank.iban)}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">BIC/SWIFT:</span>
                        <span className="font-mono font-medium text-xs">{bank.bic}</span>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-blue-600 mt-2">
                    {texts.emailNote}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>{texts.amount}</Label>
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  placeholder="100.00"
                />
              </div>

              {/* IVA breakdown for Spanish users */}
              {rechargeAmount && parseFloat(rechargeAmount) > 0 && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{texts.baseAmount}:</span>
                    <span className="font-medium">{baseAmount.toFixed(2)} €</span>
                  </div>
                  {isSpanishUser && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{texts.iva}:</span>
                      <span className="font-medium">{ivaAmount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm font-semibold border-t pt-2">
                    <span>{texts.totalToPay}:</span>
                    <span className="text-primary">{totalAmount.toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {rechargeMethod === 'bank_transfer' && (
                <div className="space-y-2">
                  <Label>{texts.reference}</Label>
                  <Input
                    value={transferReference}
                    onChange={(e) => setTransferReference(e.target.value)}
                    placeholder={texts.yourReference}
                  />
                </div>
              )}

              {/* Tax notice message */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {texts.taxNotice}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRechargeDialog(false)}>
                {texts.cancel}
              </Button>
              <Button
                onClick={rechargeMethod === 'paypal' ? handlePaypalRecharge : handleRecharge}
                disabled={submitting || !rechargeMethod || !rechargeAmount}
                data-testid="recharge-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {texts.processing}</>
                ) : rechargeMethod === 'paypal' ? (
                  <>{tx('Pagar con PayPal', 'Pay with PayPal')}</>
                ) : (
                  texts.requestTopUp
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bank Details Dialog */}
        <Dialog open={!!showBankDetails} onOpenChange={() => setShowBankDetails(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {tx('Solicitud Creada', 'Request Created')}
              </DialogTitle>
              <DialogDescription>
                {tx('Realiza la transferencia a la siguiente cuenta bancaria:', 'Make the transfer to the following bank account:')}
              </DialogDescription>
            </DialogHeader>

            {showBankDetails && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-muted rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Banco:</span>
                    <span className="font-medium">{showBankDetails.bank}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Titular:</span>
                    <span className="font-medium">{showBankDetails.holder}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">IBAN:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{showBankDetails.iban}</span>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(showBankDetails.iban)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">BIC/SWIFT:</span>
                    <span className="font-mono font-medium">{showBankDetails.bic}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Importe:</span>
                    <span className="font-bold text-primary">{rechargeAmount} €</span>
                  </div>
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  <p className="font-medium mb-1">⚠️ Importante:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Realiza la transferencia con el importe exacto</li>
                    <li>Una vez recibido el pago, aprobaremos la recarga</li>
                    <li>El saldo se añadirá automáticamente a tu monedero</li>
                  </ul>
                </div>

                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {tx('Datos bancarios enviados a tu email', 'Bank details sent to your email')}
                  </span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setShowBankDetails(null)}>
                {tx('Entendido', 'Got it')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Missing Billing Data Dialog */}
        <Dialog open={showMissingDataDialog} onOpenChange={setShowMissingDataDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-5 h-5" />
                {tx('Datos de Facturación Requeridos', 'Billing Data Required')}
              </DialogTitle>
              <DialogDescription>
                {tx('Para realizar una recarga, primero debes completar tus datos de facturación.', 
                    'To make a top-up, you must first complete your billing information.')}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  {missingDataMessage || tx(
                    'Completa todos los campos obligatorios en la sección "Mi Cuenta" para poder realizar recargas.',
                    'Complete all required fields in "My Account" section to make top-ups.')}
                </p>
              </div>
            </div>

            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowMissingDataDialog(false)}>
                {tx('Cancelar', 'Cancel')}
              </Button>
              <Button onClick={() => navigate('/my-account')} className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {tx('Ir a Mi Cuenta', 'Go to My Account')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default WalletPage;
